import React, { useEffect } from 'react';
import { usePopper } from 'react-popper';

import {
    AutoPlacement,
    BasePlacement,
    VariationPlacement,
} from '@popperjs/core/lib/enums';
import { BlackTooltip } from '@vchasno/ui-kit';

import cn from 'classnames';
import BodyPortal from 'components/BodyPortal';

import { Nullable } from '../../../types/general';

import Icon from '../icon/icon';

import SvgBorderedHint from './images/borderedhint.svg';
import SvgHint from './images/hint.svg';

import css from './hint.css';

export interface HintProps {
    icon?: React.ReactNode;
    tooltipClassName?: string;
    typeContour?: boolean;
    position?: AutoPlacement | BasePlacement | VariationPlacement;
    suppressPortal?: boolean;
    showDelay?: number;
    hideDelay?: number;
    show?: boolean;
    black?: boolean;
}

/**
 * @deprecated - use BlackTooltip from '@vchasno/ui-kit'
 */
const Hint: React.FC<React.PropsWithChildren<HintProps>> = ({
    children,
    typeContour = false,
    icon,
    tooltipClassName,
    position = 'bottom',
    suppressPortal = false,
    showDelay = 300,
    hideDelay = 300,
    black,
    show,
}) => {
    const [open, setOpened] = React.useState(false);
    const timerMouseLeave = React.useRef<ReturnType<typeof setTimeout>>();
    const timerMouseEnter = React.useRef<ReturnType<typeof setTimeout>>();
    const isMouseEnter = React.useRef(false);
    const opened = open || show;

    const [referenceElement, setReferenceElement] = React.useState<
        Nullable<HTMLDivElement>
    >(null);
    const [popperElement, setPopperElement] = React.useState<
        Nullable<HTMLDivElement>
    >(null);
    const [arrowElement, setArrowElement] = React.useState<
        Nullable<HTMLDivElement>
    >(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: position,
        modifiers: [
            { name: 'arrow', options: { element: arrowElement } },
            {
                name: 'offset',
                options: {
                    offset: [0, 8],
                },
            },
        ],
    });

    const handleMouseLeave = () => {
        isMouseEnter.current = false;
        timerMouseLeave.current = setTimeout(() => {
            if (!isMouseEnter.current) {
                setOpened(false);
            }
        }, hideDelay);
    };

    const handleMouseEnter = () => {
        isMouseEnter.current = true;
        timerMouseEnter.current = setTimeout(() => {
            if (isMouseEnter.current) {
                setOpened(true);
            }
        }, showDelay);
    };

    useEffect(() => {
        return () => {
            if (timerMouseEnter.current) {
                clearTimeout(timerMouseEnter.current);
            }
            if (timerMouseLeave.current) {
                clearTimeout(timerMouseLeave.current);
            }
        };
    }, []);

    // для простих тултипів без складної розмітки, виводимо оновлений варіант
    if (typeof children === 'string' || black) {
        return (
            <BlackTooltip placement={position} title={children}>
                <div
                    className={cn(
                        css.root,
                        typeContour ? css.contourIcon : css.icon,
                    )}
                >
                    <Icon glyph={typeContour ? SvgBorderedHint : SvgHint} />
                </div>
            </BlackTooltip>
        );
    }

    const popover = (
        <div
            onMouseLeave={handleMouseLeave}
            onMouseEnter={handleMouseEnter}
            ref={(node) => setPopperElement(node)}
            style={styles.popper}
            {...attributes.popper}
            className={cn(css.tooltip, tooltipClassName)}
        >
            {children}
            <div
                ref={(node) => setArrowElement(node)}
                style={styles.arrow}
                className={css.arrow}
            />
        </div>
    );

    return (
        <>
            <div
                ref={(node) => setReferenceElement(node)}
                className={css.root}
                tabIndex={-1}
                onMouseLeave={handleMouseLeave}
                onMouseEnter={handleMouseEnter}
                onClick={handleMouseLeave}
            >
                {icon || (
                    <div className={typeContour ? css.contourIcon : css.icon}>
                        <Icon glyph={typeContour ? SvgBorderedHint : SvgHint} />
                    </div>
                )}
            </div>
            {opened && suppressPortal && popover}
            {opened && !suppressPortal && <BodyPortal>{popover}</BodyPortal>}
        </>
    );
};

export default Hint;
